import React, { PropsWithChildren, useCallback, useContext, useMemo } from "react";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";

import { createTheme, PaletteMode, ThemeProvider as MuiThemeProvider, alpha } from "@mui/material";

declare module "@mui/material/styles" {
  interface TypeBackground {
    brand: string;
    accentPink: string;
    accentOrange: string;
    accentYellow: string;
  }
}

interface AppThemeSwitchContextValue {
  mode: PaletteMode;
  setPaletteMode(nextMode: PaletteMode): void;
}

const storageKey = "palette-mode";
const getInitialPaletteMode = (): PaletteMode => {
  const value = localStorage.getItem(storageKey);
  if (value === "light" || value === "dark") {
    return value;
  }
  return "light";
};

const AppThemeSwitchContext = React.createContext<AppThemeSwitchContextValue>({
  mode: "light",
  setPaletteMode: () => {},
});

/**
 * Theme Provider matching the Insight project's visual design.
 *
 * Provides the same colors, typography, and component styling as the
 * insight/twosion-ui project for visual consistency.
 */
export function ThemeProvider(props: PropsWithChildren) {
  const initialPaletteMode = getInitialPaletteMode();
  const [mode, setMode] = React.useState<PaletteMode>(initialPaletteMode);

  const setPaletteModeWrapper = useCallback((nextMode: PaletteMode) => {
    setMode(nextMode);
    localStorage.setItem(storageKey, nextMode);
  }, []);

  const appThemeSwitchContext = useMemo(
    () => ({
      mode,
      setPaletteMode: setPaletteModeWrapper,
    }),
    [mode, setPaletteModeWrapper]
  );

  const appTheme = useMemo(() => {
    // Insight color palette
    const paletteLight = {
      mode: "light" as const,
      primary: {
        main: "rgb(40, 109, 178)",
        dark: "rgb(24, 86, 143)",
        light: "rgb(97, 192, 237)",
        contrastText: "rgb(255, 255, 255)",
      },
      secondary: {
        main: "rgb(120, 168, 66)",
        dark: "rgb(82, 118, 49)",
        light: "rgb(148, 208, 91)",
        contrastText: "rgb(255, 255, 255)",
      },
      background: {
        default: "rgb(242, 245, 248)",
        paper: "rgb(255, 255, 255)",
        brand: "rgb(5, 36, 65)",
        accentPink: "rgb(191, 67, 132)",
        accentOrange: "rgb(244, 165, 33)",
        accentYellow: "rgb(254, 217, 0)",
      },
      text: {
        primary: "rgb(4, 36, 65)",
        secondary: "rgba(4, 36, 65, 0.65)",
        disabled: "rgba(4, 36, 65, 0.38)",
      },
      error: {
        main: "rgb(211, 47, 47)",
        dark: "rgb(198, 40, 40)",
        light: "rgb(239, 83, 80)",
      },
      warning: {
        main: "rgb(237, 108, 2)",
        dark: "rgb(230, 81, 0)",
        light: "rgb(255, 152, 0)",
        contrastText: "rgb(255, 255, 255)",
      },
      info: {
        main: "rgba(2, 136, 209)",
        dark: "rgba(1, 87, 155)",
        light: "rgb(3, 169, 244)",
        contrastText: "rgb(255, 255, 255)",
      },
      success: {
        main: "rgb(46, 125, 50)",
        dark: "rgb(27, 94, 32)",
        light: "rgb(76, 175, 80)",
        contrastText: "rgb(255, 255, 255)",
      },
    };

    const paletteDark = {
      mode: "dark" as const,
      primary: {
        main: "rgba(84, 155, 234, 1)",
        dark: "rgba(24, 95, 161, 1)",
        light: "rgb(44, 124, 198)",
        contrastText: "rgba(4, 36, 65, 0.87)",
      },
      secondary: {
        main: "rgb(119, 169, 72)",
        dark: "rgb(82, 118, 49)",
        light: "rgb(148, 208, 91)",
        contrastText: "rgba(4, 36, 65, 0.87)",
      },
      background: {
        default: "rgb(18, 18, 18)",
        paper: "rgba(35, 35, 35, 1)",
        brand: "rgb(5, 36, 65)",
        accentPink: "rgb(191, 67, 132)",
        accentOrange: "rgb(244, 165, 33)",
        accentYellow: "rgb(254, 217, 0)",
      },
      text: {
        primary: "rgb(255, 255, 255)",
        secondary: "rgba(255, 255, 255, 0.7)",
        disabled: "rgba(255, 255, 255, 0.5)",
      },
      error: {
        main: "rgb(244, 67, 54)",
        dark: "rgb(211, 47, 47)",
        light: "rgb(229, 115, 115)",
      },
      warning: {
        main: "rgb(255, 167, 38)",
        dark: "rgba(245, 124, 0)",
        light: "rgb(255, 183, 77)",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      info: {
        main: "rgb(67, 139, 205)",
        dark: "rgb(67, 139, 205)",
        light: "rgb(79, 195, 247)",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      success: {
        main: "rgb(102, 187, 106)",
        dark: "rgb(56, 142, 60)",
        light: "rgb(129, 199, 132)",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
    };

    const palette = mode === "light" ? paletteLight : paletteDark;
    const buttonHoverColor = alpha(palette.primary.main, mode === "light" ? 0.04 : 0.08);
    const buttonSelectedColor = alpha(palette.primary.main, mode === "light" ? 0.12 : 0.16);

    return createTheme({
      palette,
      typography: {
        fontFamily: "montserrat, sans-serif",
        h1: {
          fontSize: "96px",
          fontWeight: 700,
          lineHeight: "116.7%",
          letterSpacing: "-1.5px",
        },
        h2: {
          fontSize: "68px",
          fontWeight: 700,
          lineHeight: "120%",
          letterSpacing: "-0.5px",
        },
        h3: {
          fontSize: "48px",
          fontWeight: 700,
          lineHeight: "116.7%",
        },
        h4: {
          fontWeight: 700,
          fontSize: "34px",
          lineHeight: "123.5%",
          letterSpacing: "0.25px",
        },
        h5: {
          fontWeight: 700,
          fontSize: "24px",
          lineHeight: "133.4%",
        },
        h6: {
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "160%",
          letterSpacing: "0.15px",
        },
        body1: {
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "150%",
          letterSpacing: "0.15px",
        },
        body2: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "143%",
          letterSpacing: "0.17px",
        },
        subtitle1: {
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "150%",
          letterSpacing: "0.15px",
        },
        subtitle2: {
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0.1px",
        },
      },
      shape: {
        borderRadius: 8,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              textTransform: "none",
              borderRadius: theme.spacing(1),
              boxShadow: "none",
            }),
          },
        },
        MuiCard: {
          defaultProps: {
            elevation: 0,
          },
        },
        MuiPaper: {
          defaultProps: {
            elevation: mode === "light" ? 0 : 2,
          },
          styleOverrides: {
            rounded: {
              borderRadius: 8,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: () => ({
              fontSize: "13px",
              letterSpacing: 0.16,
            }),
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              ":hover": {
                background: buttonHoverColor,
              },
              "&.Mui-selected": {
                background: buttonSelectedColor,
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              minHeight: 48,
              ":hover": {
                background: buttonHoverColor,
              },
              "&.Mui-selected": {
                background: buttonSelectedColor,
              },
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              textTransform: "none",
              borderRadius: theme.spacing(1),
              "&:hover": {
                background: buttonHoverColor,
              },
              "&.Mui-selected": {
                background: buttonSelectedColor,
              },
            }),
          },
        },
        MuiTooltip: {
          defaultProps: {
            arrow: true,
            placement: "top",
            enterDelay: 500,
          },
          styleOverrides: {
            arrow: {
              color: mode === "light" ? "rgba(24, 86, 143, 1)" : "rgba(24, 95, 161, 1)",
            },
            tooltip: {
              background: mode === "light" ? "rgba(24, 86, 143, 1)" : "rgba(24, 95, 161, 1)",
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              overflow: "hidden",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: ({ theme }) => ({
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: theme.spacing(3),
              paddingBottom: theme.spacing(3),
              paddingLeft: theme.spacing(4),
              paddingRight: theme.spacing(4),
            }),
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: ({ theme }) => ({
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: theme.spacing(3),
              paddingBottom: theme.spacing(3),
              paddingLeft: theme.spacing(4),
              paddingRight: theme.spacing(4),
            }),
          },
        },
      },
    });
  }, [mode]);

  return (
    <AppThemeSwitchContext.Provider value={appThemeSwitchContext}>
      <MuiThemeProvider theme={appTheme}>{props.children}</MuiThemeProvider>
    </AppThemeSwitchContext.Provider>
  );
}

/**
 * Hook to access theme switching functionality.
 *
 * @example
 * const { mode, setPaletteMode } = useAppThemeSwitchContext();
 * setPaletteMode(mode === 'light' ? 'dark' : 'light');
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAppThemeSwitchContext = () => useContext(AppThemeSwitchContext);
